import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
} from "react";
import * as Tone from "tone";
import Slider from "./Slider";
import { Box, Button, Tip } from "grommet";

const gainToDb = (gain) => (gain <= 0 ? -Infinity : Tone.gainToDb(gain));

const Snare = React.forwardRef((props, ref) => {
  const [isKeyPressed, setIsKeyPressed] = useState(false);
  const [pitch, setPitch] = useState(17);
  const [decay, setDecay] = useState(0.4);
  const [noiseLevel, setNoiseLevel] = useState(0.56);
  const [level, setLevel] = useState(0.85);

  const membraneRef = useRef(null);
  const noiseRef = useRef(null);

  const prepareSynth = useCallback(() => {
    if (membraneRef.current && noiseRef.current) {
      return true;
    }

    if (typeof AudioBuffer === "undefined") {
      return false;
    }

    membraneRef.current = new Tone.MembraneSynth({
      pitchDecay: 0.05,
      octaves: 10,
      oscillator: { type: "sine" },
      envelope: {
        attack: 0.001,
        decay: 0.2,
        sustain: 0,
      },
    }).toDestination();

    noiseRef.current = new Tone.NoiseSynth({
      noise: {
        type: "white",
      },
      envelope: {
        attack: 0.005,
        decay: 0.1,
        sustain: 0,
      },
    }).toDestination();

    return true;
  }, []);

  useEffect(() => {
    return () => {
      membraneRef.current?.dispose();
      noiseRef.current?.dispose();
      membraneRef.current = null;
      noiseRef.current = null;
    };
  }, []);

  const resumeAudio = useCallback(async () => {
    if (Tone.context.state === "running") {
      return true;
    }
    try {
      await Tone.start();
      return Tone.context.state === "running";
    } catch (error) {
      console.warn("AudioContext start was blocked:", error);
      return false;
    }
  }, []);

  const playSynth = useCallback(
    (time, overrides = {}) => {
      if (Tone.context.state !== "running" || !prepareSynth()) {
        return;
      }

      const activeDecay = overrides.decay ?? decay;
      const levelAmount = Math.min(Math.max(level, 0), 1);
      noiseRef.current.volume.value = gainToDb(levelAmount * noiseLevel);
      membraneRef.current.volume.value = gainToDb(levelAmount * 0.85);
      membraneRef.current.envelope.decay = activeDecay;
      noiseRef.current.envelope.decay = activeDecay * 0.5;

      const stepSeconds = Tone.Time("16n").toSeconds();
      membraneRef.current.triggerAttackRelease(pitch, stepSeconds * 0.9, time);
      noiseRef.current.triggerAttackRelease(stepSeconds * 0.9, time);
    },
    [pitch, decay, noiseLevel, level, prepareSynth]
  );

  const triggerFromUI = useCallback(async () => {
    if (!prepareSynth()) {
      return;
    }

    const canPlay = await resumeAudio();
    if (!canPlay) {
      return;
    }
    playSynth();
  }, [prepareSynth, resumeAudio, playSynth]);

  useImperativeHandle(ref, () => ({
    playSynth,
    prepareSynth,
  }));

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code === "KeyS" && !isKeyPressed) {
        setIsKeyPressed(true);
        triggerFromUI();
      }
    };
    const handleKeyUp = (event) => {
      if (event.code === "KeyS") {
        setIsKeyPressed(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [isKeyPressed, triggerFromUI]);

  return (
    <Box
      align="center"
      className="instrument-panel"
      pad="small"
      responsive={false}
    >
      <Tip
        dropProps={{ align: { left: "right" } }}
        content={"Trigger with S"}
        plain
        pad="small"
      >
        <Button
          className="instrument-trigger"
          label="Snare"
          onClick={triggerFromUI}
          primary
          size="small"
        />
      </Tip>
      <Slider
        parameter={pitch}
        setParameter={setPitch}
        minValue={10}
        maxValue={100}
        stepValue={1}
        controlName="Pitch"
      />
      <Slider
        parameter={decay}
        setParameter={setDecay}
        minValue={0.01}
        maxValue={0.5}
        stepValue={0.01}
        controlName="Decay"
      />
      <Slider
        parameter={noiseLevel}
        setParameter={setNoiseLevel}
        minValue={0}
        maxValue={1}
        stepValue={0.01}
        controlName="Noise Level"
      />
      <Slider
        compact
        parameter={level}
        setParameter={setLevel}
        minValue={0}
        maxValue={1}
        stepValue={0.01}
        controlName="Level"
      />
    </Box>
  );
});

export default React.memo(Snare);
